import { useEffect, useRef, useState } from "react";
import { ArrowRight, ArrowUpRight, ChevronDown, Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Logo } from "@/components/yanre/Logo";
import { track } from "@/lib/track";
import { INSTITUTIONAL_WEBSITE_URL } from "@/config";
import { useLocale } from "@/i18n/LocaleContext";
import { LOCALES, lpPath } from "@/i18n";

const NAV_KEYS = ["Why Fitness", "Why YANRE", "How It Works", "Real Projects"];
const NAV_HREFS = ["#why-fitness", "#why-yanre", "#process", "#projects"];

// VISIT WEBSITE — always visible, secondary. Inert until INSTITUTIONAL_WEBSITE_URL
// (src/config.js) holds the verified production URL, then activates automatically.
const VisitWebsite = ({ testId, mobile = false }) => {
  const { t } = useLocale();
  const cls = `flex items-center gap-1.5 font-mono uppercase tracking-[0.2em] transition-colors ${
    mobile ? "px-5 py-4 text-xs text-titanium/45" : "text-[11px] text-titanium/45 hover:text-titanium"
  }`;
  const inner = (
    <>
      {t("nav.visitWebsite", "Visit Website")}
      <ArrowUpRight className="h-3.5 w-3.5" />
    </>
  );
  return INSTITUTIONAL_WEBSITE_URL ? (
    <a
      href={INSTITUTIONAL_WEBSITE_URL}
      target="_blank"
      rel="noopener"
      onClick={() => track("visit_website_click", { placement: mobile ? "mobile-menu" : "header" })}
      className={cls}
      data-testid={testId}
    >
      {inner}
    </a>
  ) : (
    <span role="link" aria-disabled="true" title="Institutional website — link pending" className={`${cls} cursor-default`} data-testid={testId}>
      {inner}
    </span>
  );
};

// Discreet premium language selector — preserves the full query string
// (UTMs, click IDs, campaign variant) across language navigation.
// Shared by both landing families: locales/pathFn default to the OYG family.
// Country-market mode (Africa Wave 1): pass currentLang + onSelect to switch
// the interface language IN PLACE, keeping the country route and identity.
export const LanguageSelector = ({ mobile = false, locales = LOCALES, pathFn = lpPath, currentLang, onSelect }) => {
  const { locale, lang } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const current = currentLang || lang || locale;
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  const change = (l) => {
    setOpen(false);
    if (l === current) return;
    track("language_change", { from: current, to: l });
    if (onSelect) { onSelect(l); return; }
    navigate({ pathname: pathFn(l), search: location.search });
  };

  return (
    <div ref={ref} className="relative" data-testid="language-selector">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-label="Select language"
        aria-expanded={open}
        className={`flex items-center gap-1.5 border border-carbon-600 font-mono text-[11px] uppercase tracking-[0.2em] text-titanium/70 transition-colors hover:border-titanium/50 hover:text-white ${
          mobile ? "px-4 py-3" : "px-3 py-2"
        }`}
        data-testid="language-selector-btn"
      >
        {current === "br" ? "PT-BR" : current}
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute end-0 top-full z-[70] mt-2 w-full min-w-[72px] border border-carbon-600 bg-carbon-900" data-testid="language-menu">
          {locales.map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => change(l)}
              className={`block w-full px-3 py-2.5 text-center font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${
                l === current ? "bg-signal text-white" : "text-titanium/70 hover:bg-carbon-800 hover:text-white"
              }`}
              data-testid={`lang-${l}`}
            >
              {l === "br" ? "PT-BR" : l}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Shared landing-page shell header.
export const LandingHeader = ({ ctaHref = "#project-builder" }) => {
  const { t, lang, setLang, market } = useLocale();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const logoExternal = Boolean(INSTITUTIONAL_WEBSITE_URL);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300 ${
        scrolled ? "border-carbon-700 bg-carbon-950/90 backdrop-blur-md" : "border-transparent bg-transparent"
      }`}
      data-testid="landing-header"
    >
      <div className="container-edge flex h-16 items-center justify-between lg:h-20">
        <a
          href={INSTITUTIONAL_WEBSITE_URL || "#top"}
          {...(logoExternal ? { target: "_blank", rel: "noopener" } : {})}
          aria-label="YANRE FITNESS EUROPE"
          data-testid="header-logo-link"
        >
          <Logo />
        </a>
        <nav className="hidden items-center gap-8 lg:flex" data-testid="landing-nav">
          {NAV_KEYS.map((label, i) => (
            <a
              key={NAV_HREFS[i]}
              href={NAV_HREFS[i]}
              className="font-mono text-[11px] uppercase tracking-[0.2em] text-titanium/70 transition-colors hover:text-white"
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {t(`nav.${label}`, label)}
            </a>
          ))}
          <VisitWebsite testId="nav-visit-website" />
        </nav>
        <div className="flex items-center gap-3">
          {/* Country markets: in-place language switch keeping the country
              route (Mauritius EN⇄FR); hidden when the market has one language. */}
          {market ? (
            market.langs.length > 1 && (
              <LanguageSelector locales={market.langs} currentLang={lang} onSelect={setLang} />
            )
          ) : (
            <LanguageSelector />
          )}
          <a
            href={ctaHref}
            onClick={() => track("cta_start_project", { placement: "header" })}
            className="btn-signal group !px-5 !py-3"
            data-testid="header-cta-btn"
          >
            {t("header.cta", "Start Your Project")}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 rtl:-scale-x-100" />
          </a>
          <button
            type="button"
            className="flex h-11 w-11 items-center justify-center border border-carbon-600 text-titanium lg:hidden"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            data-testid="mobile-menu-btn"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="border-t border-carbon-700 bg-carbon-950 lg:hidden" data-testid="mobile-nav">
          {NAV_KEYS.map((label, i) => (
            <a
              key={NAV_HREFS[i]}
              href={NAV_HREFS[i]}
              onClick={() => setOpen(false)}
              className="block border-b border-carbon-800 px-5 py-4 font-mono text-xs uppercase tracking-[0.2em] text-titanium/80"
            >
              {t(`nav.${label}`, label)}
            </a>
          ))}
          <VisitWebsite testId="mobile-nav-visit-website" mobile />
        </nav>
      )}
    </header>
  );
};
